import { DayPicker } from "react-day-picker";
import type { DateRange } from "react-day-picker";
import type { CivilDate } from "../../lib/civilDate";
import { civilDateAsUtcDate, civilDateFromUtcDate, civilDateInZone } from "../../lib/civilDate";

/**
 * The month grid, in this application's own vocabulary.
 *
 * ## Why the stylesheet is never imported
 *
 * `react-day-picker` ships `style.css`, and every slot's default class is an
 * inert `rdp-*` string until that file is loaded. Leaving it out and supplying a
 * complete `classNames` map means the calendar's entire appearance comes from
 * `tokens.css` — there is no second palette, no radius or shadow arriving from
 * outside, and no cascade fight between the two. It also means the map has to be
 * complete: a slot left out is a slot with no styling at all, which is why they
 * are all listed even where the class is empty.
 *
 * ## Why the whole grid runs in UTC
 *
 * A calendar picks a *civil* date — "the first of August" — and the operator's
 * browser zone has no bearing on which square they clicked. Pinning `timeZone` to
 * UTC and carrying dates as UTC midnight (`lib/civilDate`) makes the round trip
 * exact: the square labelled 1 is the date `2026-08-01`, everywhere, with no
 * offset arithmetic anywhere near the selection.
 *
 * The zone reappears one level up, where a civil date becomes an instant the
 * server can filter on — `usageWindow` resolves that in the *depot's* calendar,
 * which is a different question with its own DST tests. What this component needs
 * a zone for is one thing only: which square is today.
 *
 * ## Why state is styled on the cell and not the button
 *
 * In range mode `react-day-picker` marks every day between the endpoints
 * `selected` *as well as* `range_middle`, so a map that styled both would have
 * two background utilities of equal specificity on one element and the winner
 * would be whatever order Tailwind happened to emit. Endpoints therefore key off
 * `range_start`/`range_end`, the interior off `range_middle`, and `selected` is
 * styled only in single mode where nothing else claims it.
 */

/** Ink-on-paper chevrons. The panel has no icon set and does not need one for two arrows. */
function Chevron({ orientation }: { readonly orientation?: "left" | "right" | "up" | "down" }) {
  const path =
    orientation === "right"
      ? "M6 3l5 5-5 5"
      : orientation === "up"
        ? "M3 10l5-5 5 5"
        : orientation === "down"
          ? "M3 6l5 5 5-5"
          : "M10 3L5 8l5 5";

  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
      <path d={path} fill="none" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

const NAV_BUTTON = [
  "inline-flex h-8 w-8 items-center justify-center rounded-hairline",
  "border border-transparent text-ink-muted",
  "transition-colors duration-fast ease-swiss",
  "hover:border-rule hover:text-ink",
  "aria-disabled:text-ink-faint aria-disabled:hover:border-transparent",
].join(" ");

/** The base appearance of a day. Everything stateful is applied by the cell around it. */
const DAY_BUTTON = [
  "flex h-8 w-8 items-center justify-center rounded-hairline",
  "text-data text-ink",
  "transition-colors duration-instant ease-swiss",
  "hover:bg-paper-sunken active:translate-y-px",
].join(" ");

/**
 * The endpoints carry the app's primary weight — ink fill, paper text, hover to
 * signal — which is exactly `Button variant="primary"`, so "chosen" looks the
 * same here as everywhere else.
 */
const ENDPOINT = "[&>button]:bg-ink [&>button]:text-paper [&>button]:hover:bg-signal";

/**
 * Today is a rule under the numeral, not a fill, so today and selected can both
 * be true of one square — an operator checking whether a range reaches the
 * present needs to see both at once. It inverts on the ink fill to stay visible.
 */
const TODAY_RULE = [
  "relative after:absolute after:inset-x-1.5 after:bottom-0.5",
  "after:h-px after:bg-ink after:content-['']",
  "data-[selected=true]:after:bg-paper",
].join(" ");

const BASE_CLASS_NAMES = {
  // `relative` so the nav's `absolute` resolves here. Without it the nearest
  // positioned ancestor is whatever the calendar happens to be dropped into —
  // today the popover, which puts the arrows in roughly the right place by
  // accident, and somewhere else entirely the first time it is used inline.
  root: "relative w-fit",
  months: "flex flex-col gap-4 sm:flex-row sm:gap-6",
  month: "relative flex flex-col gap-2",

  // Month name at the margin, arrows at the far edge. Not centred between two
  // arrows: the month is a heading, and headings in this app start at the margin.
  month_caption: "flex h-8 items-center",
  caption_label: "text-section font-semibold text-ink",
  nav: "absolute right-0 top-0 flex items-center gap-1",
  button_previous: NAV_BUTTON,
  button_next: NAV_BUTTON,
  chevron: "",

  month_grid: "border-collapse",
  weekdays: "",
  // The Swiss label. `tokens.css` reserves `--text-micro` for field captions and
  // column heads, and a weekday header is a column head.
  weekday: "h-7 w-8 align-middle text-micro font-normal uppercase text-ink-muted",
  week: "",
  weeks: "",
  week_number_header: "w-8",
  week_number: "text-micro text-ink-faint",

  day: "p-0 align-middle",
  day_button: DAY_BUTTON,

  today: TODAY_RULE,
  outside: "[&>button]:text-ink-faint",
  disabled: "[&>button]:text-ink-faint [&>button]:hover:bg-transparent",
  hidden: "invisible",

  dropdowns: "flex items-center gap-2",
  dropdown_root: "relative border-b border-rule",
  dropdown: "absolute inset-0 opacity-0",
  footer: "pt-2 text-micro uppercase text-ink-muted",
} as const;

const SINGLE_CLASS_NAMES = { ...BASE_CLASS_NAMES, selected: ENDPOINT, range_middle: "" };

const RANGE_CLASS_NAMES = {
  ...BASE_CLASS_NAMES,
  // Styled by position, not by `selected`, which range mode also sets on the
  // interior. See the note at the top of this file.
  selected: "",
  range_start: ENDPOINT,
  range_end: ENDPOINT,
  // A solid ink block eight cells wide reads as one object rather than a span of
  // days. The sunken wash keeps the individual dates legible and still enclosed.
  range_middle: "[&>button]:bg-paper-sunken [&>button]:text-ink [&>button]:hover:bg-rule",
};

/** What both modes share: the calendar "today" is judged in, and how many grids to show. */
interface CalendarBase {
  /**
   * Which calendar decides what "today" is. The fleet views pass the depot's
   * zone, so an operator in another office is not shown their own Tuesday marked
   * on a Cambodian fleet's report.
   */
  readonly zone: string;
  readonly now?: Date | undefined;
  readonly months?: number | undefined;
}

export interface CalendarProps extends CalendarBase {
  readonly selected: CivilDate | undefined;
  readonly onSelect: (date: CivilDate | undefined) => void;
}

export function Calendar({ zone, now, months = 1, selected, onSelect }: CalendarProps) {
  return (
    <DayPicker
      mode="single"
      timeZone="UTC"
      classNames={SINGLE_CLASS_NAMES}
      components={{ Chevron }}
      numberOfMonths={months}
      showOutsideDays
      weekStartsOn={1}
      today={todayInGrid(zone, now)}
      {...(selected === undefined
        ? {}
        : { selected: civilDateAsUtcDate(selected), defaultMonth: civilDateAsUtcDate(selected) })}
      onSelect={(date) => onSelect(date === undefined ? undefined : civilDateFromUtcDate(date))}
    />
  );
}

export interface CalendarRangeProps extends CalendarBase {
  readonly from: CivilDate | undefined;
  readonly to: CivilDate | undefined;
  readonly onSelect: (range: { from?: CivilDate; to?: CivilDate }) => void;
}

/**
 * Two months side by side, because the ranges this is for — a month, a billing
 * period — routinely cross a month boundary. Picking one from a single grid means
 * paging forward mid-selection, which puts the start date off screen exactly when
 * the operator is deciding where to end.
 */
export function CalendarRange({ zone, now, months = 2, from, to, onSelect }: CalendarRangeProps) {
  const selected: DateRange | undefined =
    from === undefined && to === undefined
      ? undefined
      : {
          // `from` is required by `DateRange` and may genuinely be unset — an
          // operator can fill the end of a window first — so it is passed
          // explicitly undefined rather than omitted.
          from: from === undefined ? undefined : civilDateAsUtcDate(from),
          ...(to === undefined ? {} : { to: civilDateAsUtcDate(to) }),
        };

  return (
    <DayPicker
      mode="range"
      timeZone="UTC"
      classNames={RANGE_CLASS_NAMES}
      components={{ Chevron }}
      numberOfMonths={months}
      showOutsideDays
      weekStartsOn={1}
      today={todayInGrid(zone, now)}
      {...(selected === undefined ? {} : { selected })}
      {...(from === undefined ? {} : { defaultMonth: civilDateAsUtcDate(from) })}
      onSelect={(range) =>
        onSelect({
          ...(range?.from === undefined ? {} : { from: civilDateFromUtcDate(range.from) }),
          ...(range?.to === undefined ? {} : { to: civilDateFromUtcDate(range.to) }),
        })
      }
    />
  );
}

/**
 * Today, as the grid's own UTC-midnight representation.
 *
 * Deliberately not left to the calendar's default. Running in UTC it would mark
 * whatever day it is in UTC, which is the wrong day for seven hours out of every
 * twenty-four in Phnom Penh — and the day it marks is the one an operator uses to
 * check that a range reaches the present.
 */
function todayInGrid(zone: string, now: Date | undefined): Date {
  return civilDateAsUtcDate(civilDateInZone(zone, now ?? new Date()));
}
